// frontend/src/components/onboarding/stage11-run-history.tsx
//
// The Stage 11 "Run history & re-measurements" panel (rendered at case 11 in
// onboarding-stage-content.tsx). Formerly named stage10-* — renamed to kill
// the off-by-one. Replaces the old "schedule re-measurements" view.
//
// Architecture:
//   - Loads runs via listRuns() on mount + after each run completes
//   - For any in-flight run (status='pending' or 'running'), opens a
//     WebSocket to /onboarding/ws/measurement?run_id=N for live progress
//   - "Start new measurement run" expander opens BatchConfigPicker pre-filled
//     with the most recent run's config; clicking Start kicks off a new run
//     via POST /customers/{id}/runs and immediately appends a row that the
//     WS subsequently animates
//   - Each non-baseline run row computes inline deltas vs the BASELINE run
//     and shows ↑/↓ arrows next to each metric

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, CheckCircle2, AlertCircle, Loader2, Plus, ChevronDown,
  ChevronUp, FileDown, GitCompare, ArrowUp, ArrowDown, Minus, Clock,
  RefreshCw, X, Pencil, Check, Eye, EyeOff, Pin,
} from "lucide-react";
import { toast } from "sonner";

import {
  listRuns, startReRun, cancelRun, downloadRunReportPdf, downloadRunReportMd, runRunPipeline,
  renameRun, setRunSeriesInclusion, openMeasurementWs, getCustomer,
  type RunSummary, type CustomerDetail, type MeasurementWsMessage,
  type RunScoringStatus,
} from "@/api/onboarding-api";
import {
  resumeMeasurement,
  fetchMeasurementVersionPolicy,
  type MeasurementVersionPolicy,
} from "@/api/onboarding-measurement-api";
import { listScorerModels, type ScorerModel } from "@/api/onboarding-scoring-api";
import { IncompleteRunNotice } from "@/app/components/shared/incomplete-run-notice";
import { NewBaselineChip } from "@/app/components/shared/rebaseline-notice";
import { SeriesRebaselinePanel } from "./series-rebaseline-panel";
import {
  BatchConfigPicker, computeBatchCalls, computeBatchCost, toCount,
  type BatchConfig,
} from "./onboarding-batch-config";
import {
  MEASUREMENT_DEFAULT_MODELS,
  type MeasurementModel,
} from "@/app/constants/models";
import { RubricResults } from "./rubric-results";
import { SmokeCalibrationPanel } from "./smoke-calibration-panel";


// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

function fmtPct(v: number | null | undefined, decimals = 1): string {
  if (v == null) return "—";
  return `${(v * 100).toFixed(decimals)}%`;
}

function fmtPctDelta(v: number, decimals = 1): string {
  const sign = v > 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(decimals)}%`;
}

function fmtMoney(v: number | null | undefined): string {
  if (v == null) return "$0.00";
  return `$${v.toFixed(2)}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtConfig(cfg: RunSummary["batch_config"]): string {
  const q = toCount(cfg.queries, 0);
  const r = toCount(cfg.reps, 0);
  const m = (cfg.models || []).length;
  const total = cfg.total_calls ?? q * r * m;
  return `${q} queries × ${r} reps × ${m} models = ${total} calls`;
}

// Compact grounding-regime summary from the run's resolved web_search map
// (backend batch_config.web_search: { lane: boolean }). Lets a run's web-on vs
// static split be read off the row instead of git history. Returns "" for
// legacy runs that predate the field.
function fmtGrounding(cfg: RunSummary["batch_config"]): string {
  const ws = (cfg as { web_search?: Record<string, boolean> }).web_search;
  if (!ws || typeof ws !== "object") return "";
  const lanes = Object.keys(ws);
  if (lanes.length === 0) return "";
  const web = lanes.filter((l) => ws[l]).length;
  const staticN = lanes.length - web;
  if (staticN === 0) return "all web-grounded";
  if (web === 0) return "all static";
  return `${web} web · ${staticN} static`;
}

// Which call surface the Perplexity lane used for this run (#90: Sonar chat
// completions vs. the Agent API), read off the run's resolved endpoint map
// (backend batch_config.endpoint: { lane: "sonar" | "agent" }). Returns ""
// when the lane wasn't part of the run, or for legacy runs that predate the
// field.
function fmtEndpoint(cfg: RunSummary["batch_config"]): string {
  const endpoint = (cfg as { endpoint?: Record<string, string> }).endpoint;
  const perplexity = endpoint?.["perplexity"];
  if (!perplexity) return "";
  return perplexity === "agent" ? "Perplexity: Agent" : "Perplexity: Sonar";
}

// Default (auto) title when the user hasn't named the run.
function runDefaultTitle(run: RunSummary): string {
  if ((run.run_sequence_number ?? 0) === 1) return "Baseline run";
  return `Run #${run.run_sequence_number ?? "?"}`;
}

// What we show: the user's free-text name when set, else the auto title.
function runDisplayTitle(run: RunSummary): string {
  const name = (run.run_name ?? "").trim();
  return name || runDefaultTitle(run);
}

// The most recent COMPLETED run before this one (by sequence number) — the
// baseline side of a "compare to previous run" comparison. Undefined when this
// is the earliest completed run.
function previousCompletedRun(
  runs: RunSummary[],
  run: RunSummary,
): RunSummary | undefined {
  const seq = run.run_sequence_number ?? 0;
  let prev: RunSummary | undefined;
  for (const r of runs) {
    if (r.status !== "completed") continue;
    const s = r.run_sequence_number ?? 0;
    if (s >= seq) continue;
    if (!prev || s > (prev.run_sequence_number ?? 0)) prev = r;
  }
  return prev;
}

// The run a row's deltas are measured against: the series baseline on the
// original segment, or — after a series rebaseline — that segment's new
// baseline, so a delta never spans a planned change in what is measured.
function segmentBaselineFor(
  runs: RunSummary[],
  run: RunSummary,
  originalBaseline: RunSummary | undefined,
): RunSummary | undefined {
  const series = run.series;
  if (!series || series.segment === 0) return originalBaseline;
  return runs.find(
    (r) => r.series?.new_baseline && r.series.rebaseline_id === series.rebaseline_id,
  );
}

// How a run's SCORING state is labelled. Separate from statusConfig above,
// which describes COLLECTION — the two are genuinely different states and the
// row used to show only the first. A completed collection whose judge pass is
// still running rendered as a plain "Completed" row with no scoring anywhere on
// it, which is what made run 37 look unscored for six and a half hours.
function scoringChipConfig(state: RunScoringStatus["state"]) {
  return {
    not_started: {
      bg: "var(--muted)",
      fg: "var(--muted-foreground)",
      label: "Not scored",
      Icon: Minus,
      spin: false,
    },
    in_flight: {
      bg: "var(--alpha-teal-10)",
      fg: "var(--alpha-teal)",
      label: "Scoring",
      Icon: Loader2,
      spin: true,
    },
    completed: {
      bg: "rgba(34,197,94,0.10)",
      fg: "var(--chart-3)",
      label: "Scored",
      Icon: CheckCircle2,
      spin: false,
    },
    adopted: {
      bg: "rgba(34,197,94,0.10)",
      fg: "var(--chart-3)",
      label: "Scored (adopted)",
      Icon: CheckCircle2,
      spin: false,
    },
    failed: {
      bg: "rgba(220,38,38,0.10)",
      fg: "var(--infrared-coral)",
      label: "Scoring failed",
      Icon: AlertCircle,
      spin: false,
    },
    cancelled: {
      bg: "var(--muted)",
      fg: "var(--muted-foreground)",
      label: "Scoring cancelled",
      Icon: X,
      spin: false,
    },
  }[state] ?? {
    bg: "var(--muted)",
    fg: "var(--muted-foreground)",
    label: state,
    Icon: Minus,
    spin: false,
  };
}


// The transport, spelled out. "batch" and "direct" are the server's words; this
// is what they cost, which is the only reason anyone reads the field.
function fmtScoringMode(mode: RunScoringStatus["mode"]): string {
  if (mode === "batch") return "batch";
  if (mode === "direct") return "direct";
  // Never "batch (default)": a pass whose transport was not recorded is not
  // evidence that it ran on the default one.
  return "transport not recorded";
}


function scoringModeTitle(mode: RunScoringStatus["mode"]): string {
  if (mode === "batch") {
    return "Sent through the Message Batches API — half of standard token "
      + "prices, results when the batch ends.";
  }
  if (mode === "direct") {
    return "Sent as one synchronous request per response — full price, but "
      + "finishes in minutes.";
  }
  return "No transport recorded for this pass. Passes rebuilt from their "
    + "stored score rows cannot know: nothing about it survives in a row.";
}


function statusConfig(status: RunSummary["status"]) {
  return {
    pending: {
      bg: "var(--muted)",
      fg: "var(--muted-foreground)",
      label: "Pending",
      Icon: Clock,
    },
    running: {
      bg: "var(--alpha-teal-10)",
      fg: "var(--alpha-teal)",
      label: "Running",
      Icon: Loader2,
    },
    completed: {
      bg: "rgba(34,197,94,0.10)",
      fg: "var(--chart-3)",
      label: "Completed",
      Icon: CheckCircle2,
    },
    cancelled: {
      bg: "var(--muted)",
      fg: "var(--muted-foreground)",
      label: "Cancelled",
      Icon: X,
    },
    failed: {
      bg: "rgba(220,38,38,0.10)",
      fg: "var(--infrared-coral)",
      label: "Failed",
      Icon: AlertCircle,
    },
  }[status];
}


// ───────────────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────────────

interface Stage11RunHistoryProps {
  customer: CustomerDetail;
  onCustomerUpdate: (c: CustomerDetail) => void;
  onCompareRuns: (baselineRunId: number, candidateRunId: number) => void;
}

export function Stage11RunHistory({
  customer,
  onCustomerUpdate,
  onCompareRuns,
}: Stage11RunHistoryProps) {
  const [runs, setRuns] = useState<RunSummary[]>(customer.runs || []);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Run ids whose scoring/report pipeline we just (re)triggered — used to show
  // a spinner and disable the button until the WS reports completion.
  const [pipelineBusy, setPipelineBusy] = useState<Set<number>>(new Set());
  // Live scoring progress per run, mirrored from the measurement-channel
  // scoring_started/scoring_progress events that a re-measurement emits while
  // the robust Stage-5 scorer runs. Cleared when the run reaches a terminal WS
  // event. runId → {completed, total, percent}.
  const [scoringByRun, setScoringByRun] = useState<
    Map<number, { completed: number; total: number; percent: number }>
  >(new Map());

  const setScoring = useCallback(
    (runId: number, value: { completed: number; total: number; percent: number }) =>
      setScoringByRun((prev) => new Map(prev).set(runId, value)),
    [],
  );
  const clearScoring = useCallback((runId: number) => {
    setScoringByRun((prev) => {
      if (!prev.has(runId)) return prev;
      const next = new Map(prev);
      next.delete(runId);
      return next;
    });
  }, []);

  const framework = customer.framework;
  const totalQueries = Math.min(framework.queries?.length ?? 25, 25);

  // Which model versions this customer's next read defaults to, and the pins it
  // carries forward. A client with no imported baseline is read on the versions
  // its FIRST run pinned, so neither this picker's old hardcoded "baseline" nor
  // the live catalog describes what a re-read runs — only the policy does.
  const [versionPolicy, setVersionPolicy] = useState<MeasurementVersionPolicy | null>(null);
  // Bumped after a series rebaseline is applied: the policy's pins, lanes and
  // pending rebaseline all change with it.
  const [policyNonce, setPolicyNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetchMeasurementVersionPolicy(customer.id)
      .then((policy) => { if (!cancelled) setVersionPolicy(policy); })
      .catch(() => { /* non-fatal — the server still resolves it on submit */ });
    return () => { cancelled = true; };
  }, [customer.id, policyNonce]);
  // An applied series rebaseline no read has taken yet: the next client read
  // runs on its lanes and pins whatever the picker says (the server enforces).
  const pendingRebaseline = versionPolicy?.pending_rebaseline ?? null;

  // Pre-fill the picker with the most recent COMPLETED run's config —
  // or fall back to a sensible default (Smoke: 1 query × 1 rep × 1 model)
  const previousConfig = useMemo<BatchConfig>(() => {
    const lastCompleted = runs.find((r) => r.status === "completed");
    // Not carried over from the previous run: the version mode is the
    // customer's, not the run's, and a re-read of a pinned client must land on
    // "Current" (its pins) rather than inherit whatever one earlier run chose.
    const versionMode = versionPolicy?.default_mode;
    // After a series rebaseline, the series' lanes are the rebaseline's — the
    // pending one's for the read that will take it.
    const seriesLanes = (versionPolicy?.pending_rebaseline?.lanes ?? versionPolicy?.series_lanes) as
      MeasurementModel[] | undefined;
    if (lastCompleted) {
      return {
        queries: toCount(lastCompleted.batch_config.queries, totalQueries),
        reps: toCount(lastCompleted.batch_config.reps, 10),
        models: seriesLanes?.length
          ? seriesLanes
          : (lastCompleted.batch_config.models || [...MEASUREMENT_DEFAULT_MODELS]) as MeasurementModel[],
        versionMode,
      };
    }
    return {
      queries: totalQueries,
      reps: 10,
      models: seriesLanes?.length ? seriesLanes : [...MEASUREMENT_DEFAULT_MODELS],
      versionMode,
    };
  }, [runs, totalQueries, versionPolicy?.default_mode, versionPolicy?.series_lanes, versionPolicy?.pending_rebaseline]);

  const [pickerValue, setPickerValue] = useState<BatchConfig>(previousConfig);
  // Optional free-text name for the run being started from the picker.
  const [pickerName, setPickerName] = useState("");
  // Optional Stage-5 scorer (judge) for this re-measurement's automatic scoring
  // pass. "" = registry default. A re-measurement auto-scores as soon as
  // collection finishes, so pinning the judge here (vs. the manual Stage-5
  // picker, which is unreachable before the default fires) is what makes a
  // baseline scored on a non-default judge — e.g. Sunbit run 4 on Opus 4.7 —
  // reproducible in one pass.
  const [pickerScorer, setPickerScorer] = useState("");
  // Pre-flight intent. null = untouched, so the SUGGESTED value applies (see
  // preflightSuggested): an operator who never thinks about this still gets a
  // tiny probe kept off the client chart, which is the failure this exists for.
  const [pickerPreflight, setPickerPreflight] = useState<boolean | null>(null);
  const [scorerModels, setScorerModels] = useState<ScorerModel[]>([]);

  // Load the scorer registry once, when the picker is first opened.
  useEffect(() => {
    if (!showPicker || scorerModels.length > 0) return;
    let cancelled = false;
    listScorerModels()
      .then((res) => { if (!cancelled) setScorerModels(res.models || []); })
      .catch(() => { /* non-fatal — "Auto" still submits the registry default */ });
    return () => { cancelled = true; };
  }, [showPicker, scorerModels.length]);

  // Reset picker when previousConfig changes (i.e. after a new run completes)
  useEffect(() => {
    setPickerValue(previousConfig);
  }, [previousConfig]);

  const baselineRun = useMemo(() => runs.find((r) => (r.run_sequence_number ?? 0) === 1), [runs]);

  // ── Data fetch ──────────────────────────────────────────────────────
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listRuns(customer.id);
      setRuns(res.runs);
    } catch (e: any) {
      toast.error(e?.message || "Failed to load run history");
    } finally {
      setLoading(false);
    }
  }, [customer.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const clearPipelineBusy = useCallback((runId: number) => {
    setPipelineBusy((prev) => {
      if (!prev.has(runId)) return prev;
      const next = new Set(prev);
      next.delete(runId);
      return next;
    });
  }, []);

  // ── Pipeline (regenerate) completion tracking ───────────────────────
  // The scoring/report pipeline runs on a COMPLETED run, for which we don't
  // open a WebSocket — so we can't rely on the WS measurement_complete event to
  // clear the "Regenerating…" spinner. Instead, while any run is pipelineBusy,
  // poll the run list, and clear a run's busy flag once it has a report (success)
  // or an error_message (failure).
  useEffect(() => {
    if (pipelineBusy.size === 0) return;
    const interval = setInterval(() => { reload(); }, 3000);
    return () => clearInterval(interval);
  }, [pipelineBusy, reload]);

  useEffect(() => {
    if (pipelineBusy.size === 0) return;
    setPipelineBusy((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const runId of prev) {
        const r = runs.find((x) => x.id === runId);
        if (r && (r.has_report || r.error_message)) {
          next.delete(runId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [runs, pipelineBusy]);

  // Keep the list fresh while the SERVER says a judge pass is running.
  //
  // The WebSocket only covers a pass this page watched start; reload mid-pass
  // and there is nothing driving the row. Without this the state the run list
  // now carries would be correct but frozen — the banner would sit at whatever
  // the page happened to load with until someone pressed Refresh, which is a
  // milder version of the same defect.
  //
  // Ten seconds, not the three the pipeline poll uses: that one is waiting on
  // an action an operator just took, this one on a pass that routinely runs for
  // tens of minutes (a batch pass stores nothing at all until its batch ends).
  const anyScoringInFlight = useMemo(
    () => runs.some((r) => r.scoring?.in_flight), [runs],
  );
  useEffect(() => {
    if (!anyScoringInFlight) return;
    const interval = setInterval(() => { reload(); }, 10000);
    return () => clearInterval(interval);
  }, [anyScoringInFlight, reload]);

  // Prune stale scoring bars: if a reload (e.g. manual Refresh mid-scoring)
  // brings a run that has since gained a report or errored, drop its live
  // scoring entry so the bar doesn't linger frozen.
  useEffect(() => {
    if (scoringByRun.size === 0) return;
    setScoringByRun((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const runId of prev.keys()) {
        const r = runs.find((x) => x.id === runId);
        if (r && (r.has_report || r.error_message)) {
          next.delete(runId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [runs, scoringByRun]);

  // ── WebSocket tracking for in-flight runs ───────────────────────────
  // For each pending/running run, open a WS and merge progress events
  // back into the local runs[] state. Closes WS on completion.
  const wsByRun = useRef<Map<number, WebSocket>>(new Map());

  useEffect(() => {
    const inflight = runs.filter((r) => r.status === "pending" || r.status === "running");

    // Open WS for any in-flight run we're not already tracking
    for (const run of inflight) {
      if (wsByRun.current.has(run.id)) continue;

      const ws = openMeasurementWs({
        customerId: customer.id,
        runId: run.id,
        onEvent: (msg) => handleWsMessage(run.id, msg),
        onClose: () => {
          wsByRun.current.delete(run.id);
        },
      });
      wsByRun.current.set(run.id, ws);
    }

    // Close WS for any run no longer in-flight (completed/cancelled/failed)
    for (const [runId, ws] of wsByRun.current.entries()) {
      const stillRunning = inflight.some((r) => r.id === runId);
      if (!stillRunning) {
        try { ws.close(); } catch { /* noop */ }
        wsByRun.current.delete(runId);
      }
    }

    // Cleanup on unmount
    return () => {
      for (const ws of wsByRun.current.values()) {
        try { ws.close(); } catch { /* noop */ }
      }
      wsByRun.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs.map((r) => `${r.id}:${r.status}`).join("|"), customer.id]);

  const handleWsMessage = useCallback((runId: number, msg: MeasurementWsMessage) => {
    switch (msg.type) {
      case "snapshot": {
        // The server's initial snapshot — make sure we have the run
        // already; nothing to merge here since listRuns gives us the row.
        return;
      }
      case "measurement_progress": {
        setRuns((prev) =>
          prev.map((r) =>
            r.id === runId
              ? {
                  ...r,
                  status: "running",
                  progress: {
                    completed: msg.completed,
                    total: msg.total,
                  },
                  cost_usd: msg.cost_so_far,
                }
              : r,
          ),
        );
        return;
      }
      case "measurement_finished": {
        // Battery done; auto-pipeline now running. Mark as "completed
        // measurement, scoring in progress" via a sentinel — still show as running.
        setRuns((prev) =>
          prev.map((r) =>
            r.id === runId
              ? {
                  ...r,
                  status: "running", // still "running" because auto-pipeline next
                  progress: {
                    completed: msg.completed,
                    total: msg.total,
                  },
                  cost_usd: msg.cost_usd,
                }
              : r,
          ),
        );
        return;
      }
      case "auto_pipeline_started":
      case "heartbeat":
        return;
      case "scoring_started": {
        // Measurement collection is done; the robust scorer just began. Keep the
        // row "running" and seed a 0% scoring bar.
        setScoring(runId, { completed: 0, total: msg.total, percent: 0 });
        setRuns((prev) =>
          prev.map((r) => (r.id === runId ? { ...r, status: "running" } : r)),
        );
        return;
      }
      case "scoring_progress": {
        setScoring(runId, {
          completed: msg.completed,
          total: msg.total,
          percent: msg.percent,
        });
        return;
      }
      case "measurement_complete": {
        // Final state — mark completed + reload run history so the
        // server-side artifacts (scoring, validation, report) are picked up.
        // cost_usd is absent on a pipeline-only completion (regen path), so
        // guard it — an undefined .toFixed() would throw and skip the reload.
        const costSuffix =
          typeof msg.cost_usd === "number" ? ` ($${msg.cost_usd.toFixed(2)})` : "";
        toast.success(`Run completed${costSuffix}`);
        clearPipelineBusy(runId);
        clearScoring(runId);
        reload();
        // Also refresh customer detail so any other panels reflect the change
        if (msg.customer) onCustomerUpdate(msg.customer);
        return;
      }
      case "measurement_cancelled": {
        toast.info("Run cancelled");
        clearPipelineBusy(runId);
        clearScoring(runId);
        reload();
        return;
      }
      case "auto_pipeline_failed": {
        toast.error(`Scoring/report failed: ${msg.message}`);
        // Collection itself is still ok — leave status as completed; reload
        // picks up the run with measurement intact but missing scoring/report,
        // and the card surfaces the error + a retry button.
        clearPipelineBusy(runId);
        clearScoring(runId);
        reload();
        return;
      }
      case "error": {
        toast.error(msg.message || "Run failed");
        reload();
        return;
      }
    }
  }, [reload, onCustomerUpdate, clearPipelineBusy, setScoring, clearScoring]);

  // ── Pre-flight detection ────────────────────────────────────────────
  // The planned size of this run against the biggest measurement this customer
  // has actually completed. Below SERIES_SCORED_FLOOR of it, the run cannot be
  // compared to the others and the server will not chart it — so pre-tick
  // "pre-flight" and say so, instead of letting it look like a measurement until
  // a client chart disagrees. The operator can always untick it.
  const SERIES_SCORED_FLOOR = 0.5;
  const largestRunCalls = useMemo(
    () => Math.max(
      0,
      ...runs
        .filter((r) => r.status === "completed")
        .map((r) => r.scored_count ?? r.progress?.total ?? 0),
    ),
    [runs],
  );
  const plannedCalls = computeBatchCalls(pickerValue);
  const preflightSuggested =
    largestRunCalls > 0 &&
    plannedCalls < Math.max(1, Math.floor(largestRunCalls * SERIES_SCORED_FLOOR));
  const preflightChecked = pickerPreflight ?? preflightSuggested;

  // ── Actions ─────────────────────────────────────────────────────────
  const handleStartRun = async () => {
    setSubmitting(true);
    try {
      const res = await startReRun(customer.id, {
        batch_config: {
          queries: pickerValue.queries,
          reps: pickerValue.reps,
          models: pickerValue.models,
          // No hardcoded "baseline": for a client pinned to its own first read
          // that mode means the IMPORTED baseline's versions (GPT-5.4 …), not
          // this client's. Undefined -> omitted, and the server resolves the
          // customer's own default.
          version_mode: pickerValue.versionMode ?? versionPolicy?.default_mode,
          model_overrides: pickerValue.modelOverrides,
          web_search_overrides: pickerValue.webSearchOverrides,
          endpoint_overrides: pickerValue.endpointOverrides,
          scorer_model_id: pickerScorer || undefined,
        },
        run_name: pickerName.trim() || undefined,
        preflight: preflightChecked,
      });
      toast.success(`${res.run.run_name?.trim() || `Run #${res.run.run_sequence_number ?? "?"}`} started.`);
      setShowPicker(false);
      setPickerName("");
      setPickerScorer("");
      setPickerPreflight(null);
      // Optimistically append the new run so the WS effect kicks in
      setRuns((prev) => [res.run, ...prev]);
    } catch (e: any) {
      toast.error(e?.message || "Failed to start run");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenameRun = async (runId: number, name: string) => {
    // Optimistic: reflect the new name immediately, roll back on failure.
    const prevRuns = runs;
    const cleaned = name.trim();
    setRuns((prev) =>
      prev.map((r) => (r.id === runId ? { ...r, run_name: cleaned || null } : r)),
    );
    try {
      await renameRun(customer.id, runId, cleaned);
    } catch (e: any) {
      setRuns(prevRuns);
      toast.error(e?.message || "Failed to rename run");
    }
  };

  // Three states, because two were not enough: "auto" is not the same as "shown".
  // A run returned to "auto" is shown only if it is a real measurement whose
  // scored-row count clears the floor — which is exactly what a 6-row pre-flight
  // probe does not do. Forcing a small run onto a client chart now needs the
  // explicit "include", so it can never happen by default again.
  const handleSetSeriesInclusion = async (
    runId: number,
    inclusion: "auto" | "include" | "exclude",
  ) => {
    // Optimistic: reflect the new state immediately, roll back on failure.
    const prevRuns = runs;
    setRuns((prev) =>
      prev.map((r) => (r.id === runId
        ? {
            ...r,
            series_inclusion: inclusion,
            excluded_from_series: inclusion === "exclude",
            // The server recomputes eligibility (the floor is relative to the
            // customer's largest run); only "exclude" is certain locally.
            series_eligible: inclusion === "exclude" ? false : r.series_eligible,
          }
        : r)),
    );
    try {
      const { run } = await setRunSeriesInclusion(customer.id, runId, inclusion);
      // Adopt the server's recomputed verdict rather than guessing at it.
      setRuns((prev) => prev.map((r) => (r.id === runId ? { ...r, ...run } : r)));
      toast.success(
        inclusion === "exclude" ? "Run hidden from the Performance chart."
        : inclusion === "include" ? "Run pinned onto the Performance chart."
        : "Run handed back to the automatic rules.",
      );
    } catch (e: any) {
      setRuns(prevRuns);
      toast.error(e?.message || "Failed to update chart visibility");
    }
  };

  const handleCancelRun = async (runId: number) => {
    try {
      await cancelRun(runId);
      toast.info("Cancel requested — worker will stop at next batch.");
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId ? { ...r, cancellation_requested: true } : r,
        ),
      );
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel run");
    }
  };

  const handleResumeRun = async (runId: number) => {
    try {
      const res = await resumeMeasurement(customer.id, runId);
      toast.info("Resuming run from its last checkpoint…");
      // Optimistically flip the row back to running so the WS effect re-attaches
      // and the progress bar reappears; the backend continues from the exact
      // offset (work_items[progress_completed:]).
      setRuns((prev) =>
        prev.map((r) =>
          r.id === runId
            ? {
                ...r,
                status: "running",
                error_message: null,
                cancellation_requested: false,
                progress: {
                  completed: res.run.progress_completed,
                  total: res.run.progress_total,
                },
                cost_usd: res.run.cost_so_far_usd,
              }
            : r,
        ),
      );
    } catch (e: any) {
      toast.error(e?.message || "Failed to resume run");
    }
  };

  const handleRunPipeline = async (runId: number) => {
    setPipelineBusy((prev) => new Set(prev).add(runId));
    try {
      await runRunPipeline(customer.id, runId);
      toast.info("Scoring / report generation started…");
      // Optimistically clear any stale error so the card shows the busy state.
      setRuns((prev) =>
        prev.map((r) => (r.id === runId ? { ...r, error_message: null } : r)),
      );
    } catch (e: any) {
      toast.error(e?.message || "Failed to start scoring/report");
      clearPipelineBusy(runId);
    }
  };

  const handleDownloadPdf = async (run: RunSummary) => {
    try {
      await downloadRunReportPdf(customer.id, run.id, run.run_sequence_number);
    } catch (e: any) {
      toast.error(e?.message || "Failed to download PDF");
    }
  };

  const handleDownloadMd = async (run: RunSummary) => {
    try {
      await downloadRunReportMd(customer.id, run.id, run.run_sequence_number);
    } catch (e: any) {
      toast.error(e?.message || "Failed to download Markdown");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      className="rounded-xl"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div>
          <h3 style={{
            fontSize: "var(--text-base)", fontWeight: 700,
            color: "var(--foreground)", margin: 0,
          }}>
            Measurement runs
          </h3>
          <p style={{
            fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
            marginTop: 2, margin: 0,
          }}>
            {runs.length === 0
              ? "No runs yet."
              : `${runs.length} run${runs.length === 1 ? "" : "s"} total · `
                + `${runs.filter((r) => r.status === "completed").length} completed`}
          </p>
        </div>
        <button
          onClick={reload}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md"
          style={{
            backgroundColor: "transparent",
            color: "var(--muted-foreground)",
            border: "1px solid var(--border)",
            fontSize: "var(--text-xs)",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Run list */}
      <div className="p-3 md:p-4 space-y-3">
        {runs.length === 0 && !loading && (
          <p style={{
            fontSize: "var(--text-sm)", color: "var(--muted-foreground)",
            textAlign: "center", padding: "24px 12px",
          }}>
            Start the first measurement run below.
          </p>
        )}
        {runs.map((run) => (
          <RunRow
            key={run.id}
            run={run}
            customerId={customer.id}
            reasoningVariables={framework?.reasoning_variables}
            baseline={segmentBaselineFor(runs, run, baselineRun)}
            previousRun={previousCompletedRun(runs, run)}
            onDownload={() => handleDownloadPdf(run)}
            onDownloadMd={() => handleDownloadMd(run)}
            onCompare={(otherRunId) => onCompareRuns(otherRunId, run.id)}
            onCancel={() => handleCancelRun(run.id)}
            onResume={() => handleResumeRun(run.id)}
            onRunPipeline={() => handleRunPipeline(run.id)}
            pipelineBusy={pipelineBusy.has(run.id)}
            scoring={scoringByRun.get(run.id)}
            onRename={(name) => handleRenameRun(run.id, name)}
            onSetSeriesInclusion={(inclusion) => handleSetSeriesInclusion(run.id, inclusion)}
          />
        ))}
      </div>

      {/* Series rebaselines: history, the open draft, and apply. */}
      <SeriesRebaselinePanel
        customerId={customer.id}
        onApplied={() => { reload(); setPolicyNonce((n) => n + 1); }}
      />

      {/* Inline new-run expander */}
      <div
        className="border-t"
        style={{ borderColor: "var(--border)" }}
      >
        {!showPicker ? (
          <div className="flex flex-col">
            <button
              onClick={() => setShowPicker(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 transition-colors"
              style={{
                background: "transparent",
                color: "var(--alpha-teal)",
                fontSize: "var(--text-sm)",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "var(--alpha-teal-10)")}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <Plus className="w-4 h-4" />
              Start new measurement run
              <ChevronDown className="w-3.5 h-3.5" />
            </button>

            {/* Backfill ChatGPT (Web) baseline — pre-fills a gptweb-only run at
                the baseline run's query/rep shape, so D10+ reruns have a clean,
                directly comparable ChatGPT (Web) baseline to compare against. */}
            {baselineRun && (
              <button
                onClick={() => {
                  setPickerValue({
                    queries: toCount(baselineRun.batch_config.queries, totalQueries),
                    reps: toCount(baselineRun.batch_config.reps, 10),
                    models: ["gptweb"] as MeasurementModel[],
                  });
                  setShowPicker(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 transition-colors"
                style={{
                  background: "transparent",
                  color: "var(--muted-foreground)",
                  fontSize: "var(--text-xs)",
                  fontWeight: 600,
                  borderTop: "1px solid var(--border)",
                  cursor: "pointer",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "var(--muted)")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                title="Run a ChatGPT (Web)-only measurement matching the baseline's shape"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Backfill ChatGPT (Web) baseline
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 md:p-5">
            {/* Every read needs its rubric and question battery to have passed
                Smoke calibration (#138); a read on an uncalibrated pair is
                refused, so the calibration lives next to the Start button. A
                verification (pre-flight) run is not a read and is not gated. */}
            <SmokeCalibrationPanel
              customerId={customer.id}
              frameworkApproved={!!customer.framework_approved}
              selection={pickerValue}
              onFrameworkChanged={() => {
                void getCustomer(customer.id).then(onCustomerUpdate).catch(() => undefined);
              }}
            />
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 style={{
                  fontSize: "var(--text-sm)", fontWeight: 700,
                  color: "var(--foreground)", margin: 0,
                }}>
                  New measurement run
                </h4>
                <p style={{
                  fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
                  marginTop: 2, margin: 0,
                }}>
                  Pre-filled with the previous run's config — adjust as needed.
                </p>
              </div>
              <button
                onClick={() => setShowPicker(false)}
                className="p-1 rounded"
                style={{
                  background: "transparent",
                  color: "var(--muted-foreground)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-3">
              <label
                htmlFor="run-name-input"
                style={{
                  display: "block", fontSize: 11, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  color: "var(--muted-foreground)", marginBottom: 4,
                }}
              >
                Run name (optional)
              </label>
              <input
                id="run-name-input"
                type="text"
                value={pickerName}
                maxLength={255}
                onChange={(e) => setPickerName(e.target.value)}
                placeholder="e.g. Smoke test, Q1 baseline, post-launch re-run"
                className="w-full"
                style={{
                  padding: "8px 12px", fontSize: "var(--text-sm)",
                  color: "var(--foreground)", backgroundColor: "var(--background)",
                  border: "1px solid var(--border)", borderRadius: 8, outline: "none",
                }}
              />
              <p style={{ fontSize: 11, color: "var(--muted-foreground)", margin: "4px 0 0 0" }}>
                A friendly label so this run isn't just "Run #{(runs[0]?.run_sequence_number ?? 0) + 1}". You can rename it later.
              </p>
            </div>

            {pendingRebaseline && !preflightChecked && (
              <div
                className="mb-3 rounded-lg p-3"
                style={{
                  fontSize: 12, lineHeight: 1.5,
                  backgroundColor: "var(--spectral-violet-5)",
                  border: "1px solid var(--spectral-violet-30)",
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  This read applies the series rebaseline (battery v{pendingRebaseline.battery_version}) and
                  becomes the new baseline.
                </div>
                <div style={{ color: "var(--muted-foreground)" }}>
                  It runs on the rebaseline's lanes ({pendingRebaseline.lanes.join(", ")}) and pins
                  {Object.keys(pendingRebaseline.pins).length
                    ? ` (${Object.entries(pendingRebaseline.pins).map(([l, v]) => `${l} ${v}`).join(", ")})`
                    : ""}
                  , whatever is picked below; per-lane version overrides on those lanes are ignored.
                  Reps, questions and the judge are still yours to set.
                </div>
              </div>
            )}

            <BatchConfigPicker
              totalQueries={totalQueries}
              value={pickerValue}
              onChange={setPickerValue}
              versionPins={versionPolicy?.pins}
              firstReadDefaults={versionPolicy?.first_read_defaults}
              versionPinned={versionPolicy?.pinned}
            />

            {/* Scorer (judge) — pins the model that auto-scores this run. Left on
                Auto (registry default) unless reproducing a baseline judged on a
                non-default model, e.g. Sunbit run 4 on Opus 4.7. */}
            <div
              className="mt-2 rounded-lg p-3"
              style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--foreground)" }}>
                    Scorer (judge)
                  </div>
                  <div style={{ fontSize: 10, color: "var(--muted-foreground)", marginTop: 2 }}>
                    model that grades this run — pin to reproduce a baseline&apos;s judge
                  </div>
                </div>
                <select
                  value={pickerScorer}
                  onChange={(e) => setPickerScorer(e.target.value)}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 8px",
                    borderRadius: 6,
                    border: `1px solid ${pickerScorer ? "var(--alpha-teal)" : "var(--border)"}`,
                    color: pickerScorer ? "var(--alpha-teal)" : "var(--foreground)",
                    backgroundColor: "var(--muted)",
                    cursor: "pointer",
                    maxWidth: 200,
                  }}
                >
                  <option value="">
                    Auto{scorerModels.length
                      ? ` — ${scorerModels.find((m) => m.is_default)?.label ?? "default judge"}`
                      : " — default judge"}
                  </option>
                  {scorerModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pre-flight: this run is a pipeline check, not a measurement. Kept
                off the client Performance chart for good, whatever its label. */}
            <div
              className="mt-2 rounded-lg p-3"
              style={{
                backgroundColor: "var(--card)",
                border: `1px solid ${preflightChecked ? "var(--alpha-teal-25)" : "var(--border)"}`,
              }}
            >
              <label className="flex items-start gap-2.5" style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={preflightChecked}
                  onChange={(e) => setPickerPreflight(e.target.checked)}
                  style={{ marginTop: 2, cursor: "pointer", accentColor: "var(--alpha-teal)" }}
                />
                <span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "var(--foreground)" }}>
                    Pre-flight check — keep off the client Performance chart
                  </span>
                  <span style={{ display: "block", fontSize: 10, color: "var(--muted-foreground)", marginTop: 2 }}>
                    {preflightSuggested
                      ? `${plannedCalls} calls is well under this brand's usual ${largestRunCalls}, so this looks like a pipeline check rather than a measurement. Pre-ticked — untick it if this really is a measurement.`
                      : "Tick this for a probe you are running to test the pipeline. The run and its data are kept in full; it just never becomes a point on the client chart."}
                  </span>
                </span>
              </label>
            </div>

            <div
              className="mt-4 pt-3 flex items-center justify-between"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <span style={{
                fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
              }}>
                {plannedCalls} calls · est. {fmtMoney(computeBatchCost(pickerValue))}
                {preflightChecked ? " · pre-flight (not charted)" : ""}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPicker(false)}
                  disabled={submitting}
                  className="px-3 py-1.5 rounded-md"
                  style={{
                    backgroundColor: "transparent",
                    color: "var(--muted-foreground)",
                    fontSize: "var(--text-sm)",
                    fontWeight: 500,
                    border: "1px solid var(--border)",
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartRun}
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-md"
                  style={{
                    backgroundColor: "var(--alpha-teal)",
                    color: "white",
                    fontSize: "var(--text-sm)",
                    fontWeight: 600,
                    border: "none",
                    cursor: submitting ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  {submitting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Play className="w-4 h-4" />}
                  Start run
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Individual run row
// ───────────────────────────────────────────────────────────────────────────

function RunRow({
  run, customerId, reasoningVariables, baseline, previousRun, onDownload, onDownloadMd, onCompare, onCancel, onResume, onRunPipeline, pipelineBusy, scoring, onRename, onSetSeriesInclusion,
}: {
  run: RunSummary;
  customerId: number;
  reasoningVariables?: { id: string; claim?: string }[];
  baseline: RunSummary | undefined;
  previousRun: RunSummary | undefined;
  onDownload: () => void;
  onDownloadMd: () => void;
  onCompare: (otherRunId: number) => void;
  onCancel: () => void;
  onResume: () => void;
  onRunPipeline: () => void;
  pipelineBusy: boolean;
  scoring?: { completed: number; total: number; percent: number };
  onRename: (name: string) => void;
  onSetSeriesInclusion: (inclusion: "auto" | "include" | "exclude") => void;
}) {
  const sc = statusConfig(run.status);
  // A series rebaseline's first read is a baseline too (the new one): no
  // deltas against a read measured on a different battery.
  const isBaseline = (run.run_sequence_number ?? 0) === 1 || !!run.series?.new_baseline;
  const hasCustomName = !!(run.run_name ?? "").trim();

  // Inline rename editor state.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(run.run_name ?? "");
  const beginRename = () => {
    setNameDraft(run.run_name ?? "");
    setEditingName(true);
  };
  const commitRename = () => {
    setEditingName(false);
    if ((nameDraft.trim() || "") !== ((run.run_name ?? "").trim())) {
      onRename(nameDraft);
    }
  };
  const isInflight = run.status === "pending" || run.status === "running";
  const isCompleted = run.status === "completed";

  // Scoring state, from the server. Not derived from has_scoring here: that is
  // "a pass FINISHED", and the gap between the two is exactly the window this
  // row used to render as "never scored" (run 37: 14:59-21:26 UTC on a batch
  // pass). `scoring` below is the live WS progress, which only exists if this
  // page was already open when the pass started — the server state is what a
  // page loaded mid-pass has to rely on.
  const scoringStatus = run.scoring;
  const scoringInFlight = !!scoringStatus?.in_flight;
  const scoringMode = scoringStatus?.mode ?? run.scoring_mode ?? null;
  // Shown for everything except a run that was never collected: "Not scored"
  // on a pending run is noise, not information.
  const showScoringChip = !!scoringStatus
    && (isCompleted || scoringInFlight || scoringStatus.state !== "not_started");

  // Recovery affordance for the specific "stuck" signature the old inline
  // pipeline produced: a completed run that already has scoring but never got
  // its report, or whose scoring/report pipeline explicitly failed. This is a
  // targeted fix for runs orphaned by that bug (e.g. Iterate run 12).
  //
  // Healthy LATER runs complete the decoupled pipeline on their own and end up
  // with has_report=true, so neither condition holds and the button stays
  // hidden for them — no permanent manual button cluttering the normal flow.
  const pipelineFailed = isCompleted && !run.has_report && !!run.error_message;
  const pipelineStuck =
    isCompleted && !run.has_report && (run.has_scoring || pipelineFailed);

  // A failed/cancelled run can be resumed from its last checkpoint when it
  // got partway through but not all the way. Mirrors the Stage 4 panel and the
  // backend guard in resume_measurement_run (failed|cancelled + partial only).
  const isResumable =
    (run.status === "failed" || run.status === "cancelled") &&
    run.progress.completed > 0 &&
    run.progress.completed < run.progress.total;
  const remaining = run.progress.total - run.progress.completed;

  const showDeltas = isCompleted && !isBaseline && baseline?.headline_metrics && run.headline_metrics;

  const progress = run.progress;
  const percent = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <div
      className="rounded-lg p-3 md:p-4"
      style={{
        backgroundColor: "var(--background)",
        border: "1px solid var(--border)",
      }}
    >
      {/* Top row: title, status, config */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {editingName ? (
              <span className="flex items-center gap-1">
                <input
                  autoFocus
                  type="text"
                  value={nameDraft}
                  maxLength={255}
                  placeholder={runDefaultTitle(run)}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename();
                    if (e.key === "Escape") setEditingName(false);
                  }}
                  style={{
                    padding: "2px 8px", fontSize: "var(--text-sm)", fontWeight: 700,
                    color: "var(--foreground)", backgroundColor: "var(--background)",
                    border: "1px solid var(--alpha-teal)", borderRadius: 6, outline: "none",
                    minWidth: 180,
                  }}
                />
                <button
                  onClick={commitRename}
                  title="Save name"
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--alpha-teal)", padding: 2 }}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingName(false)}
                  title="Cancel"
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--muted-foreground)", padding: 2 }}
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ) : (
              <>
                <span style={{
                  fontSize: "var(--text-sm)", fontWeight: 700,
                  color: "var(--foreground)",
                }}>
                  {runDisplayTitle(run)}
                </span>
                {/* Keep the sequence reference visible when a custom name is set,
                    so "Run #N" is still findable. */}
                {hasCustomName && (
                  <span style={{ fontSize: 11, color: "var(--muted-foreground)", fontWeight: 600 }}>
                    {runDefaultTitle(run)}
                  </span>
                )}
                <button
                  onClick={beginRename}
                  title="Rename run"
                  className="flex items-center"
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--muted-foreground)", padding: 2 }}
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </>
            )}
            <span
              className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
              style={{
                backgroundColor: sc.bg,
                color: sc.fg,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <sc.Icon className={`w-3 h-3 ${run.status === "running" ? "animate-spin" : ""}`} />
              {sc.label}
              {run.cancellation_requested && run.status === "running" && " (cancelling)"}
            </span>
            {/* Scoring state — a SECOND chip, because collection completing and
                scoring completing are different events and the row only ever
                showed the first. Carries the transport, since that is what
                explains the judge bill. */}
            {showScoringChip && scoringStatus && (() => {
              const cc = scoringChipConfig(scoringStatus.state);
              const judge = scoringStatus.scorer_model;
              return (
                <span
                  className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                  title={[
                    judge ? `Judge: ${judge}` : null,
                    scoringStatus.state === "not_started"
                      ? null : scoringModeTitle(scoringStatus.mode),
                    scoringStatus.error_message,
                  ].filter(Boolean).join(" ")}
                  style={{
                    backgroundColor: cc.bg,
                    color: cc.fg,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  <cc.Icon className={`w-3 h-3 ${cc.spin ? "animate-spin" : ""}`} />
                  {cc.label}
                  {scoringMode ? ` · ${fmtScoringMode(scoringMode)}` : ""}
                </span>
              );
            })()}
            <IncompleteRunNotice completeness={run.completeness} variant="chip" />
            <NewBaselineChip series={run.series} />
          </div>
          <p style={{
            fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
            margin: 0,
          }}>
            {fmtConfig(run.batch_config)} · {fmtMoney(run.cost_usd)}
            {fmtGrounding(run.batch_config) ? ` · ${fmtGrounding(run.batch_config)}` : ""}
            {fmtEndpoint(run.batch_config) ? ` · ${fmtEndpoint(run.batch_config)}` : ""}
            {run.triggered_by ? ` · by ${run.triggered_by}` : ""}
          </p>
          <p style={{
            fontSize: 11, color: "var(--muted-foreground)",
            margin: 0, marginTop: 2,
          }}>
            {fmtDate(run.finished_at || run.started_at || run.created_at)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isCompleted && run.has_report && (
            <button
              onClick={onDownload}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{
                backgroundColor: "transparent",
                color: "var(--alpha-teal)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                border: "1px solid var(--alpha-teal-25)",
                cursor: "pointer",
              }}
            >
              <FileDown className="w-3.5 h-3.5" />
              PDF
            </button>
          )}
          {isCompleted && run.has_report && (
            <button
              onClick={onDownloadMd}
              title="Report as Markdown, with the belief block chosen for this read"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{
                backgroundColor: "transparent",
                color: "var(--alpha-teal)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                border: "1px solid var(--alpha-teal-25)",
                cursor: "pointer",
              }}
            >
              <FileDown className="w-3.5 h-3.5" />
              MD
            </button>
          )}
          {isCompleted && !isBaseline && baseline && (
            <button
              onClick={() => onCompare(baseline.id)}
              title={`Compare against the locked baseline (Run #${baseline.run_sequence_number ?? "?"})`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{
                backgroundColor: "var(--alpha-teal)",
                color: "white",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              <GitCompare className="w-3.5 h-3.5" />
              vs baseline
            </button>
          )}
          {/* Run-over-run: compare against the most recent prior completed run.
              Hidden when that run IS the baseline (the button above already
              covers it), so we never show two identical comparisons. */}
          {isCompleted && previousRun && previousRun.id !== baseline?.id && (
            <button
              onClick={() => onCompare(previousRun.id)}
              title={`Compare against the previous run (Run #${previousRun.run_sequence_number ?? "?"})`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{
                backgroundColor: "transparent",
                color: "var(--alpha-teal)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                border: "1px solid var(--alpha-teal-25)",
                cursor: "pointer",
              }}
            >
              <GitCompare className="w-3.5 h-3.5" />
              vs prev
            </button>
          )}
          {/* Performance-chart membership, cycling auto → exclude → include.
              Display-only: the run and its data stay put either way.

              The label states what is TRUE, not what was asked for: on "auto" a
              run below the scored-row floor reads "Not charted", because the old
              two-state button said "In chart" for a 6-row pre-flight probe that
              had no business being plotted — and then said it again after the
              probe was un-hidden. */}
          {isCompleted && (() => {
            const inclusion = run.series_inclusion ?? (run.excluded_from_series ? "exclude" : "auto");
            const charted = run.series_eligible ?? !run.excluded_from_series;
            const next = inclusion === "auto" ? "exclude" : inclusion === "exclude" ? "include" : "auto";
            const label =
              inclusion === "exclude" ? "Hidden"
              : inclusion === "include" ? "Pinned on"
              : charted ? "In chart" : "Not charted";
            const why =
              run.series_exclusion_reason === "below_scored_floor"
                ? `Too few scored rows to compare: ${run.scored_count ?? 0} of the ${run.series_scored_floor ?? 0} this customer's chart requires. A pre-flight probe looks like this.`
              : run.series_exclusion_reason === "preflight_label"
                ? "Pre-flight / calibration runs are never charted."
              : run.series_exclusion_reason === "admin_excluded"
                ? "Hidden from the chart by an admin."
              : run.series_exclusion_reason === "not_a_candidate"
                ? "Not a chart candidate: no completed scoring pass."
              : inclusion === "include"
                ? "Pinned onto the chart by an admin, overriding the scored-row floor."
              : "Charted automatically: a real measurement that clears the scored-row floor.";
            const nextLabel =
              next === "exclude" ? "hide it" : next === "include" ? "pin it on anyway" : "hand it back to the rules";
            const Icon = inclusion === "exclude" ? EyeOff : inclusion === "include" ? Pin : charted ? Eye : EyeOff;
            const accent = charted ? "var(--alpha-teal)" : "var(--muted-foreground)";
            return (
              <button
                onClick={() => onSetSeriesInclusion(next)}
                title={`${why} Click to ${nextLabel}.`}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
                style={{
                  backgroundColor: "transparent",
                  color: accent,
                  fontSize: "var(--text-xs)",
                  fontWeight: 600,
                  border: `1px solid ${charted ? "var(--alpha-teal-25)" : "var(--border)"}`,
                  cursor: "pointer",
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })()}
          {isInflight && !run.cancellation_requested && (
            <button
              onClick={onCancel}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{
                backgroundColor: "transparent",
                color: "var(--infrared-coral)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                border: "1px solid var(--infrared-coral)",
                cursor: "pointer",
              }}
            >
              <Pause className="w-3.5 h-3.5" />
              Cancel
            </button>
          )}
          {isResumable && (
            <button
              onClick={onResume}
              title={`Continue from ${run.progress.completed} / ${run.progress.total} — ${remaining} calls left`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{
                backgroundColor: "var(--alpha-teal)",
                color: "white",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              <Play className="w-3.5 h-3.5" />
              Resume ({remaining} left)
            </button>
          )}
        </div>
      </div>

      {/* Progress bar (in-flight runs) */}
      {isInflight && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span style={{
              fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
            }}>
              {progress.completed} / {progress.total} calls · {percent.toFixed(0)}%
            </span>
          </div>
          <div
            className="rounded-full overflow-hidden"
            style={{
              height: 6,
              backgroundColor: "var(--muted)",
            }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${percent}%`,
                backgroundColor: "var(--alpha-teal)",
              }}
            />
          </div>
        </div>
      )}

      {/* Scoring progress — shown while the robust Stage-5 scorer runs for a
          re-measurement (between collection finishing and the report building). */}
      {scoring && (
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span style={{
              fontSize: "var(--text-xs)", color: "var(--alpha-teal)", fontWeight: 600,
            }}>
              Scoring responses · {scoring.completed} / {scoring.total} · {scoring.percent.toFixed(0)}%
            </span>
          </div>
          <div
            className="rounded-full overflow-hidden"
            style={{ height: 6, backgroundColor: "var(--muted)" }}
          >
            <div
              className="h-full transition-all"
              style={{ width: `${scoring.percent}%`, backgroundColor: "var(--alpha-teal)" }}
            />
          </div>
        </div>
      )}

      {/* The same thing for a page that was NOT open when the pass started.
          The block above is fed by the measurement WebSocket, so it exists only
          for a pass this browser watched begin; reload mid-pass and it was
          gone, leaving a bare "Completed" row — the whole reported defect. This
          one is fed by the run list itself, which now carries the state, the
          transport, the judge and the start time. */}
      {!scoring && scoringInFlight && scoringStatus && (() => {
        const scored = scoringStatus.scored_rows ?? 0;
        const expected = scoringStatus.expected_rows ?? 0;
        const pct = expected > 0
          ? Math.min(100, (scored / expected) * 100)
          : null;
        return (
          <div className="mt-2">
            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
              <span style={{
                fontSize: "var(--text-xs)", color: "var(--alpha-teal)",
                fontWeight: 600,
              }}>
                <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" />
                Scoring in progress
                {expected > 0 ? ` · ${scored} / ${expected}` : ""}
                {pct !== null ? ` · ${pct.toFixed(0)}%` : ""}
              </span>
              <span
                title={scoringModeTitle(scoringStatus.mode)}
                style={{ fontSize: 11, color: "var(--muted-foreground)" }}
              >
                {fmtScoringMode(scoringStatus.mode)}
                {scoringStatus.scorer_model ? ` · ${scoringStatus.scorer_model}` : ""}
                {scoringStatus.started_at
                  ? ` · started ${fmtDate(scoringStatus.started_at)}` : ""}
              </span>
            </div>
            <div
              className="rounded-full overflow-hidden"
              style={{ height: 6, backgroundColor: "var(--muted)" }}
            >
              {/* A batch pass stores nothing until its batch ends, so 0% here
                  is normal and does NOT mean stalled. The bar is deliberately
                  left at 0 rather than faked forward. */}
              <div
                className="h-full transition-all"
                style={{
                  width: `${pct ?? 0}%`,
                  backgroundColor: "var(--alpha-teal)",
                }}
              />
            </div>
          </div>
        );
      })()}

      {/* Headline metrics (completed runs) */}
      {isCompleted && run.headline_metrics && (
        <div
          className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2"
          style={{
            paddingTop: 12,
            borderTop: "1px solid var(--border)",
          }}
        >
          <MetricCell
            label="Mention"
            value={run.headline_metrics.mention_rate}
            delta={showDeltas
              ? run.headline_metrics.mention_rate - (baseline!.headline_metrics!.mention_rate)
              : undefined}
          />
          <MetricCell
            label="Top-5"
            value={run.headline_metrics.top5_rate}
            delta={showDeltas
              ? run.headline_metrics.top5_rate - (baseline!.headline_metrics!.top5_rate)
              : undefined}
          />
          <MetricCell
            label="Primary"
            value={run.headline_metrics.primary_rec_rate}
            delta={showDeltas
              ? run.headline_metrics.primary_rec_rate - (baseline!.headline_metrics!.primary_rec_rate)
              : undefined}
          />
          <MetricCell
            label="#1"
            value={run.headline_metrics.number_one_rate}
            delta={showDeltas
              ? run.headline_metrics.number_one_rate - (baseline!.headline_metrics!.number_one_rate)
              : undefined}
          />
        </div>
      )}
      {/* Incomplete run: the rates above rest on fewer answered calls than
          the run paid for. Renders nothing for a complete run. */}
      {isCompleted && run.headline_metrics && (
        <IncompleteRunNotice completeness={run.completeness} />
      )}

      {/* Rubric results — the same viewer used in Stage 5, so a completed run's
          per-variable scores, distribution and per-response evidence are
          reviewable directly from the run history (collapsed by default). */}
      {/* Which pass this row's numbers come from, and what it cost to send.
          The chip above says "Scored · batch"; this says on what judge, against
          which rubric, and when. */}
      {isCompleted && run.has_scoring && scoringStatus && (
        <p
          title={scoringModeTitle(scoringStatus.mode)}
          style={{
            fontSize: 11, color: "var(--muted-foreground)",
            margin: 0, marginTop: 10,
          }}
        >
          {scoringStatus.state === "adopted"
            ? "Pass adopted (no judge re-run)"
            : "Judged"}
          {scoringStatus.scorer_model ? ` · ${scoringStatus.scorer_model}` : ""}
          {` · ${fmtScoringMode(scoringStatus.mode)}`}
          {scoringStatus.rubric_version ? ` · ${scoringStatus.rubric_version}` : ""}
          {scoringStatus.finished_at ? ` · ${fmtDate(scoringStatus.finished_at)}` : ""}
        </p>
      )}

      {isCompleted && run.has_scoring && (
        <div className="mt-3">
          <RubricResults
            customerId={customerId}
            runId={run.id}
            reasoningVariables={reasoningVariables}
            rubricVersion={run.rubric_version ?? undefined}
            scorerModel={run.scorer_model ?? undefined}
            title="Rubric results — per variable, distribution & evidence"
            defaultExpanded={false}
          />
        </div>
      )}

      {/* Error message — collection failure OR scoring/report pipeline failure
          (the latter on a run whose status is still 'completed'). */}
      {run.error_message && (run.status === "failed" || pipelineFailed) && (
        <div
          className="mt-3 p-2 rounded"
          style={{
            backgroundColor: "rgba(220,38,38,0.06)",
            border: "1px solid rgba(220,38,38,0.20)",
          }}
        >
          <p style={{
            fontSize: "var(--text-xs)", color: "var(--infrared-coral)",
            margin: 0,
          }}>
            {run.error_message}
          </p>
        </div>
      )}

      {/* Recovery affordance — shown ONLY for the stuck signature (scored but
          no report, or an explicit pipeline failure), or while a regenerate the
          user kicked off is running. Healthy later runs never render this. */}
      {/* ...and never while a pass is actually running. pipelineStuck cannot
          co-occur with an in-flight pass (both of its arms require a stored
          summary or a recorded error, either of which clears in-flight), but
          pipelineBusy can — and this button is one click from a SECOND paid
          judge pass beside the one already in flight. */}
      {(pipelineStuck || pipelineBusy) && !scoring && !scoringInFlight && (
        <div
          className="mt-3 flex items-center justify-between gap-3 flex-wrap"
          style={{ paddingTop: 10, borderTop: "1px solid var(--border)" }}
        >
          <p style={{
            fontSize: "var(--text-xs)",
            color: pipelineFailed ? "var(--infrared-coral)" : "var(--muted-foreground)",
            margin: 0,
          }}>
            {pipelineBusy
              ? "Regenerating report…"
              : pipelineFailed
                ? "Scoring/report did not finish — retry below."
                : "Scoring complete — report was not generated."}
          </p>
          <button
            onClick={onRunPipeline}
            disabled={pipelineBusy}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
            style={{
              backgroundColor: pipelineBusy ? "var(--muted)" : "var(--alpha-teal)",
              color: pipelineBusy ? "var(--muted-foreground)" : "white",
              fontSize: "var(--text-xs)",
              fontWeight: 600,
              border: "none",
              cursor: pipelineBusy ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {pipelineBusy
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5" />}
            {run.has_scoring ? "Regenerate report" : "Run scoring / report"}
          </button>
        </div>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Metric cell with optional delta indicator
// ───────────────────────────────────────────────────────────────────────────

function MetricCell({
  label, value, delta,
}: {
  label: string;
  value: number;
  delta?: number;
}) {
  const hasDelta = delta !== undefined && Math.abs(delta) > 0.0005; // > 0.05 percentage point
  const isUp = (delta ?? 0) > 0;

  return (
    <div>
      <div style={{
        fontSize: 10, color: "var(--muted-foreground)",
        fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
        marginBottom: 2,
      }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <span style={{
          fontSize: "var(--text-base)", fontWeight: 700,
          color: "var(--foreground)",
        }}>
          {fmtPct(value)}
        </span>
        {hasDelta && (
          <span
            className="flex items-center gap-0.5"
            style={{
              fontSize: 11, fontWeight: 600,
              color: isUp ? "var(--chart-3)" : "var(--infrared-coral)",
            }}
          >
            {isUp
              ? <ArrowUp className="w-3 h-3" />
              : <ArrowDown className="w-3 h-3" />}
            {fmtPctDelta(Math.abs(delta!))}
          </span>
        )}
        {delta !== undefined && !hasDelta && (
          <span
            className="flex items-center"
            style={{
              fontSize: 11, color: "var(--muted-foreground)",
            }}
          >
            <Minus className="w-3 h-3" />
          </span>
        )}
      </div>
    </div>
  );
}